type PinterestCommand = 'load' | 'page' | 'track';
type PinterestCommandName = PinterestCommand | (string & {});

type PinterestEvent
  = | 'AddPaymentInfo'
    | 'AddToCart'
    | 'AddToWishList'
    | 'Checkout'
    | 'Contact'
    | 'CustomizeProduct'
    | 'FindLocation'
    | 'InitiateCheckout'
    | 'Lead'
    | 'PageVisit'
    | 'Schedule'
    | 'Search'
    | 'SignUp'
    | 'StartTrial'
    | 'Subscribe'
    | 'SubmitApplication'
    | 'ViewCategory'
    | 'ViewContent'
    | 'WatchVideo'
    | (string & {});

type PinterestLineItem = {
  product_id: string;
  product_name?: string;
  product_category?: string;
  product_variant_id?: string;
  product_variant?: string;
  product_price?: number;
  product_quantity?: number;
};

type PinterestEventPayload = {
  event_id?: string;
  value?: number;
  order_quantity?: number;
  order_id?: string;
  currency?: string;
  search_query?: string;
  lead_type?: string;
  line_items?: PinterestLineItem[];
} & Record<string, string | number | boolean | PinterestLineItem[] | undefined>;

type PinterestLoadPayload = {
  em?: string;
  external_id?: string;
  fp_cookie?: boolean;
  fp_localStorage?: boolean;
};

type PinterestQueueEntry = [
  command: PinterestCommandName,
  ...args: unknown[],
];

interface PinterestApi {
  (...args: unknown[]): void;
  queue: PinterestQueueEntry[];
  version: string;
}

interface PinterestWindow {
  pintrk?: PinterestApi;
}

function createPinterestApi(): PinterestApi {
  const api = ((...args: unknown[]) => {
    api.queue.push(args as PinterestQueueEntry);
  }) as PinterestApi;

  api.queue = [];
  api.version = '3.0';

  return api;
}

export default defineNuxtPlugin({
  name: 'kygunco:pinterest',
  dependsOn: ['kygunco:bus'],
  parallel: true,

  setup: () => {
    const config = useRuntimeConfig().public.pinterest;
    const { $bus } = useNuxtApp();

    const enabled = config.enabled && !!config.tagId;

    const pinterestWindow = window as unknown as PinterestWindow;

    const push = (command: PinterestCommandName, ...args: unknown[]) => {
      if (!enabled) {
        return;
      }

      pinterestWindow.pintrk ??= createPinterestApi();
      pinterestWindow.pintrk(command, ...args);
    };

    const track = (event: PinterestEvent, payload?: PinterestEventPayload) => {
      if (payload === undefined) {
        push('track', event);

        return;
      }

      push('track', event, payload);
    };

    const init = (payload?: PinterestLoadPayload) => payload === undefined
      ? push('load', config.tagId)
      : push('load', config.tagId, payload);

    const { load, onError } = useScript(
      {
        src: 'https://s.pinimg.com/ct/core.js',
        referrerpolicy: false,
        crossorigin: false,
      },
      {
        trigger: 'manual',
        beforeInit: () => {
          if (import.meta.server || !enabled) {
            return;
          }

          pinterestWindow.pintrk ??= createPinterestApi();

          init();
        },
      },
    );

    /**
     * Pinterest tracking is non-critical. Fail silently when its script cannot
     * be loaded so analytics cannot interrupt the application.
     */
    onError(noop);

    useScriptEventPage(() => push('page'));

    $bus.once('app:interacted', () => enabled && load());

    $bus.on('product:view', (product, variation) =>
      track('PageVisit', {
        value: variation?.price ?? product.price,
        currency: 'USD',
        line_items: [
          {
            product_id: product.id,
            product_name: product.name,
            product_variant_id: variation?.id?.toString(),
            product_price: variation?.price ?? product.price,
            product_quantity: 1,
          },
        ],
      }),
    );

    $bus.on('product:add', (product, variation, quantity) =>
      track('AddToCart', {
        value: (variation?.price ?? product.price) * (quantity ?? 1),
        order_quantity: quantity ?? 1,
        currency: 'USD',
        line_items: [
          {
            product_id: product.id,
            product_name: product.name,
            product_variant_id: variation?.id?.toString(),
            product_price: variation?.price ?? product.price,
            product_quantity: quantity ?? 1,
          },
        ],
      }),
    );

    $bus.on('catalog:add', (product, _collection) => {
      track('AddToCart', {
        value: product.price,
        order_quantity: 1,
        currency: 'USD',
        line_items: [
          {
            product_id: product.id,
            product_name: product.name,
            product_price: product.price,
            product_quantity: 1,
          },
        ],
      });
    });

    $bus.on('checkout:start', (items) => {
      track('InitiateCheckout', {
        value: items.reduce((total, item) => total + item.price * item.quantity, 0),
        order_quantity: items.reduce((total, item) => total + item.quantity, 0),
        currency: 'USD',
        line_items: items.map(item => ({
          product_id: item.productId,
          product_price: item.price,
          product_quantity: item.quantity,
        })),
      });
    });

    $bus.on('checkout:complete', (orders) => {
      for (const order of orders) {
        track('Checkout', {
          event_id: order.id,
          order_id: order.id,
          value: order.grandTotal,
          order_quantity: order.items.reduce((total, item) => total + item.quantity, 0),
          currency: 'USD',
          line_items: order.items.map(item => ({
            product_id: item.productId,
            product_price: item.price,
            product_quantity: item.quantity,
          })),
        });
      }
    });

    return {
      provide: {
        pinterest: {
          enabled,
          track,
          push,
        },
      },
    };
  },
});
